import { z } from "zod";

import type { Viewer } from "../data/viewer.ts";

type Audience = "everyone" | "managers" | "owner";
export type SettingsGroup = "access" | "account" | "household" | "records";

interface SectionSpec {
  readonly group: SettingsGroup;
  readonly audience: Audience;
}

const spec = (group: SettingsGroup, audience: Audience): SectionSpec => ({ group, audience });

// WHY: the route parses sections in the first load; their labels live in section-labels.ts, which loads with the pages.
const settingsSections = {
  profile: spec("account", "everyone"),
  security: spec("account", "everyone"),
  notifications: spec("account", "everyone"),
  "calendar-feeds": spec("account", "everyone"),
  appearance: spec("account", "everyone"),
  general: spec("household", "managers"),
  properties: spec("household", "managers"),
  "asset-kinds": spec("household", "managers"),
  "running-costs": spec("household", "managers"),
  templates: spec("household", "managers"),
  manual: spec("household", "managers"),
  members: spec("access", "managers"),
  "share-links": spec("access", "managers"),
  "audit-log": spec("records", "owner"),
  export: spec("records", "owner"),
  trash: spec("records", "managers"),
} as const satisfies Record<string, SectionSpec>;

export type SettingsSection = keyof typeof settingsSections;

const isSection = (id: string): id is SettingsSection => Object.hasOwn(settingsSections, id);

const sectionIds = Object.keys(settingsSections).filter((id) => isSection(id));

export const settingsSectionSchema = z.enum(sectionIds);

const isInAudience = (audience: Audience, viewer: Viewer): boolean =>
  audience === "everyone" ||
  viewer.role === "owner" ||
  (audience === "managers" && viewer.role === "manager");

export const canOpenSection = (section: SettingsSection, viewer: Viewer): boolean =>
  isInAudience(settingsSections[section].audience, viewer);

export const visibleSections = (viewer: Viewer): readonly (readonly [SettingsSection, SectionSpec])[] =>
  sectionIds.filter((id) => canOpenSection(id, viewer)).map((id) => [id, settingsSections[id]] as const);
